import React from "react";
import { Link } from "react-router-dom";
import "./PrivacyPage.css";

/** Terms of Service — plain-English, matching the Privacy page design. */

const TermsPage: React.FC = () => (
  <div className="pp-page">
    <div className="pp-wrap">
      <Link to="/" className="pp-back">← Back to Everything Eco</Link>
      <span className="pp-eyebrow">📋 The fine print, made friendly</span>
      <h1>Terms of Service</h1>
      <p className="pp-updated">Last updated: July 7, 2026</p>

      <div className="pp-card">
        <p>
          Welcome to Everything Eco NYC. By using our website and booking a pickup, you agree to these
          terms. We've kept them clear and human — because that's how we do things.
        </p>

        <h2>1. Who we are</h2>
        <p>
          Everything Eco NYC provides free recycling and donation pickups for electronics, textiles,
          furniture, and household goods across New York City's five boroughs, along with paid bulk
          services for businesses. "We," "us," and "our" refer to Everything Eco NYC.
        </p>

        <h2>2. Our free pickups</h2>
        <ul>
          <li>Home pickups for e-waste, clothing, and household donations are 100% free for New Yorkers.</li>
          <li>We serve Brooklyn, Manhattan, Queens, the Bronx, and Staten Island. Availability may vary by neighborhood and demand.</li>
          <li>When you book, you'll receive a confirmation. We'll do our best to arrive within your chosen window, but times are estimates and may change due to weather, traffic, or scheduling.</li>
          <li>We may decline or reschedule pickups — for example, if items fall outside what we accept or if a location isn't safely accessible.</li>
        </ul>

        <h2>3. Business and bulk services</h2>
        <p>
          Office cleanouts and large bulk pickups may involve a quote. We'll always share pricing upfront
          and in writing before any paid work begins — no hidden fees.
        </p>

        <h2>4. What we accept — and don't</h2>
        <p>
          We accept electronics, e-waste, clothing, textiles, furniture, household goods, and food
          donations. We <strong>cannot</strong> accept hazardous materials such as paint, chemicals, or
          other hazardous waste. If you're unsure about an item, just ask before booking.
        </p>

        <h2>5. Your responsibilities</h2>
        <ul>
          <li>Provide accurate pickup details and make items reasonably accessible for our team.</li>
          <li>Remove any personal belongings you want to keep before pickup.</li>
          <li>Back up and remove data you wish to keep from devices. If you'd like data destruction, request it when booking.</li>
          <li>Make sure you have the right to donate the items you're giving us.</li>
        </ul>

        <h2>6. Ownership of donated items</h2>
        <p>
          Once we collect an item, ownership transfers to Everything Eco. We may refurbish, donate,
          resell, or responsibly recycle it as part of our mission. We can't return items after pickup,
          so please double-check before handing anything off.
        </p>

        <h2>7. Donation receipts</h2>
        <p>
          Qualifying donations may come with a tax-deductible receipt through our nonprofit partners.
          Receipts are subject to partner eligibility, and we don't provide tax advice — please consult
          your own advisor.
        </p>

        <h2>8. EcoPoints and rewards</h2>
        <p>
          EcoPoints and rewards are offered as a thank-you and have no cash value. We may adjust, expire,
          or change the rewards program at any time. Points earned through fraud or abuse may be revoked.
        </p>

        <h2>9. The Eco-Force universe and shop</h2>
        <p>
          Comics, figures, games, and other merchandise are offered subject to availability and separate
          purchase terms shown at checkout. "Coming soon" items are not guaranteed and pricing may change.
        </p>

        <h2>10. Intellectual property</h2>
        <p>
          The Everything Eco name, logo, characters (including Earth Jr. and the Eco-Allies), artwork,
          and content are our property. Please don't copy or reuse them without permission.
        </p>

        <h2>11. Service "as is" and limitation of liability</h2>
        <p>
          We provide our website and pickups on a reasonable-effort, "as is" basis. To the fullest extent
          allowed by law, Everything Eco isn't liable for indirect or incidental damages arising from use
          of our site or services. Nothing here limits liability that can't be limited by law.
        </p>

        <h2>12. Changes to these terms</h2>
        <p>
          We may update these terms as we grow. The current version will always live here with an updated
          date. Continued use of our services means you accept the latest version.
        </p>

        <h2>13. Contact us</h2>
        <ul>
          <li>Email: <a href="mailto:everythingeconyc@gmail.com">everythingeconyc@gmail.com</a></li>
          <li>Everything Eco NYC · Williamsburg, Brooklyn · New York City</li>
        </ul>
      </div>
    </div>
  </div>
);

export default TermsPage;
